import React, { createContext, useContext, useState, useCallback } from 'react';

const MerchantStoreContext = createContext();

export function MerchantStoreProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [likedProducts, setLikedProducts] = useState([]);

  // ✅ NEW: cache per-store metrics by owner username
  // Shape: { [ownerUsername]: { avgRating: number, completedOrders: number, fetchedAt: number } }
  const [storeMetrics, setStoreMetrics] = useState({});

  // helper to upsert a store's metrics
  const upsertStoreMetrics = useCallback((ownerUsername, metrics) => {
    if (!ownerUsername || !metrics) return;
    setStoreMetrics(prev => ({
      ...prev,
      [ownerUsername]: {
        avgRating: Number(metrics.avgRating ?? 0),
        completedOrders: Number(metrics.completedOrders ?? 0),
        fetchedAt: Date.now(),
      }
    }));
  }, []);

  // optional: get metrics with a simple TTL (default 5 minutes)
  const getStoreMetrics = useCallback((ownerUsername, { ttlMs = 5 * 60 * 1000 } = {}) => {
    const entry = ownerUsername ? storeMetrics[ownerUsername] : undefined;
    if (!entry) return null;
    if (ttlMs && Date.now() - entry.fetchedAt > ttlMs) return null;
    return entry;
  }, [storeMetrics]);

  const toggleLike = async (productId, api, username) => {
    try {
      await api.post(`/products/${productId}/like`, { username });

      setLikedProducts(prev =>
        prev.includes(productId)
          ? prev.filter(id => id !== productId)
          : [...prev, productId]
      );

      setProducts(prev =>
        prev.map(p =>
          p._id === productId
            ? {
                ...p,
                likes: prev.includes(productId)
                  ? p.likes.filter(u => u !== username)
                  : [...p.likes, username],
              }
            : p
        )
      );
    } catch (err) {
      console.error('❌ Failed to toggle like:', err);
    }
  };

  return (
    <MerchantStoreContext.Provider
      value={{
        products, setProducts,
        likedProducts, setLikedProducts, toggleLike,
        // ✅ expose metrics APIs
        storeMetrics, upsertStoreMetrics, getStoreMetrics,
      }}
    >
      {children}
    </MerchantStoreContext.Provider>
  );
}

export function useMerchantStore() {
  return useContext(MerchantStoreContext);
}
